import React, { useCallback, useState } from 'react';
import {
    adminCreateAgency,
    adminUpdateAgencyByUniqueIdentifier,
    updateCurrentAgency
} from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';
import { Agency } from '../../core/_models';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';

export default function ManageAgencyForm({
    agency = undefined,
    setAgency = undefined,
    isForCurrentAgency = false,
}: {
    agency?: Agency,
    setAgency?: (agency: Agency) => void,
    isForCurrentAgency?: boolean,
}) {
    const axios = useAxios();
    const navigate = useNavigate();

    const {
        currentUser
    } = useAuth();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    // Advanced mode state
    const [viewAdvancedOptions, setViewAdvancedOptions] = useState(false);

    const onManageAgencyFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        setSaveSuccess(null);
        setErrorSaving(null);
        setSaving(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        const finalData = {
            ...data,
            // disable free trial is a boolean but comes in as "on" or "off" from the checkbox
            manually_disabled_free_trial: formData.get('manually_disabled_free_trial') === 'on',
            enable_free_access: formData.get('enable_free_access') === 'on',
        }
        if (!agency) {
            try {
                const createdAgency = await adminCreateAgency(axios, finalData);
                navigate(`/admin/agencies/${createdAgency.unique_identifier}/setup-device`);
            } catch (exception) {
                console.error(exception);
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.';
                setErrorSaving(errorMessage);
                setSaving(false);
            }
        } else {
            try {
                if (isForCurrentAgency) {
                    await updateCurrentAgency(axios, finalData);
                    window.location.reload();
                } else {
                    const updatedAgency = await adminUpdateAgencyByUniqueIdentifier(axios, agency.unique_identifier, finalData);
                    if (!setAgency) {
                        throw new Error('setAgency function is required to update agency');
                    }
                    setAgency(updatedAgency);
                    setSaveSuccess('Agency updated successfully');
                }
            } catch (exception) {
                console.error(exception);
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.';
                setErrorSaving(errorMessage);
            } finally {
                setSaving(false);
            }
        }
    }, [saving, agency, axios, navigate, setAgency, isForCurrentAgency]);

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
            <div className="col-span-2">
                <div className="flex flex-col gap-5 lg:gap-7.5">
                    <form className="card" onSubmit={onManageAgencyFormSubmitted}>
                        <div className="card-header">
                            <h3 className="card-title">
                                Enter agency details
                            </h3>
                        </div>
                        <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                            <div className="flex flex-col gap-4">
                                {/* Always show name field */}
                                <div>
                                    <label
                                        htmlFor="agency_name"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {`Name this Agency (required)`}
                                    </label>
                                    <input
                                        id="agency_name"
                                        name="agency_name"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder="Mogul Clients"
                                        required
                                        autoComplete='off'
                                        autoFocus={!agency}
                                        defaultValue={agency?.name || ''}
                                    />
                                </div>
                                {/* See manage account form for more fields */}
                            </div>
                        </div>
                        <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                            <div>
                                {errorSaving && <div className="text-red-500 text-sm font-medium">{errorSaving}</div>}
                                {saveSuccess && <div className="text-green-500 text-sm font-medium">{saveSuccess}</div>}
                            </div>
                            <div>
                                {agency ? <button
                                    type="submit"
                                    className="btn btn-sm btn-primary"
                                    disabled={saving}
                                >
                                    {!saving ? `Update Agency` : `Updating Agency...`}
                                </button> : <button
                                    type="submit"
                                    className="btn btn-sm btn-primary"
                                    disabled={saving}
                                >
                                    {!saving ? `Add Agency` : `Adding Agency...`}
                                </button>}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
